import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Search, Shield } from 'lucide-react';

export const ProcessSlider = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      icon: <FileText className="h-12 w-12" />,
      title: 'Submit Your Details',
      description: 'Fill out our easy form with your trademark information. Our experts review it within 24 hours.',
    },
    {
      icon: <Search className="h-12 w-12" />,
      title: 'Detailed Search',
      description: 'We perform a comprehensive search to confirm your trademark is unique and free from conflicts.',
    },
    {
      icon: <Shield className="h-12 w-12" />,
      title: 'File & Protect',
      description: 'We submit your application to the USPTO and track it until your brand receives full protection.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [steps.length]);

  return (
    <div className="relative" data-testid="process-slider">
      {/* Desktop View - All Steps */}
      <div className="hidden md:grid md:grid-cols-3 gap-8">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.2 }}
            className="relative"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 h-full">
              <div className="bg-[#E1A334]/10 rounded-xl w-20 h-20 flex items-center justify-center text-[#E1A334] mb-6">
                {step.icon}
              </div>
              <div className="absolute top-8 left-8 bg-[#E1A334] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                {index + 1}
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
                {step.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {step.description}
              </p>
            </div>
            {index < steps.length - 1 && (
              <div className="hidden lg:block absolute top-16 -right-4 w-8 h-0.5 bg-[#E1A334]/30" />
            )}
          </motion.div>
        ))}
      </div>

      {/* Mobile View - Slider */}
      <div className="md:hidden">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100"
        >
          <div className="bg-[#E1A334]/10 rounded-xl w-20 h-20 flex items-center justify-center text-[#E1A334] mb-6 relative">
            {steps[currentStep].icon}
            <div className="absolute -top-2 -left-2 bg-[#E1A334] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
              {currentStep + 1}
            </div>
          </div>
          <h3 className="font-heading font-bold text-xl text-slate-900 mb-3">
            {steps[currentStep].title}
          </h3>
          <p className="text-slate-600 leading-relaxed">
            {steps[currentStep].description}
          </p>
        </motion.div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {steps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep ? 'w-8 bg-[#E1A334]' : 'w-2 bg-slate-300'
              }`}
              data-testid={`process-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
